import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Eye, EyeOff, LogIn, AlertTriangle } from 'lucide-react';
import LoginBackdrop from '../components/LoginBackdrop';

export default function Login() {
  const { tenantSlug } = useParams();
  const rememberKey = `gastify_remember_user_${tenantSlug}`;
  const [username, setUsername] = useState('');
  const [senha, setSenha] = useState('');
  const [lembrar, setLembrar] = useState(false);
  const [showSenha, setShowSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { tenant, loading: tenantLoading, error: tenantError } = useTenant();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const saved = localStorage.getItem(rememberKey);
      if (saved) { setUsername(saved); setLembrar(true); }
    } catch {}
  }, [rememberKey]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !senha) { setError('Preencha todos os campos.'); return; }
    setLoading(true); setError('');
    try {
      await login(username, senha);
      try {
        if (lembrar) localStorage.setItem(rememberKey, username);
        else localStorage.removeItem(rememberKey);
      } catch {}
      navigate(`/${tenantSlug}/dashboard`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Usuário ou senha inválidos');
    } finally { setLoading(false); }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-700" />
      </div>
    );
  }

  if (tenantError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--app-bg)] p-4" data-testid="tenant-error-page">
        <div className="fin-card p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={28} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">
            {tenantError === 'not_found' ? 'Página não encontrada' : tenantError === 'suspended' ? 'Acesso suspenso' : 'Erro de carregamento'}
          </h1>
          <p className="text-sm text-slate-500">
            {tenantError === 'not_found' && 'Este endereço não existe ou foi removido.'}
            {tenantError === 'suspended' && 'Seu acesso está suspenso. Entre em contato com o administrador.'}
            {tenantError === 'unknown' && 'Não foi possível verificar o acesso. Tente novamente.'}
          </p>
        </div>
      </div>
    );
  }

  const primary = tenant.corPrimaria || '#0147a0';

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative bg-slate-900 overflow-hidden"
      data-testid="login-page"
    >
      <LoginBackdrop image="/login-bg.jpg" />
      {/* overlay para melhorar contraste do card */}
      <div className="absolute inset-0 bg-slate-950/10 pointer-events-none" />
      <div className="w-full max-w-sm relative z-10">
        <div className="glass-card rounded-none p-8 page-enter">
          <div className="text-center mb-7">
            <img
              src={tenant.logo || '/gastify-mark.png'}
              alt={tenant.nomeDoSistema || 'Gastify'}
              className="w-24 h-24 rounded-2xl object-cover mx-auto mb-4 shadow-lg border border-white/30"
              data-testid="login-logo"
            />
            <h1 className="text-2xl font-bold tracking-tight text-white glass-text-shadow" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {tenant.nomeDoSistema || 'Gastify'}
            </h1>
            <p className="text-sm text-white/80 mt-1 glass-text-shadow">{tenant.nomeEmpresa || 'Cada gasto, uma decisão consciente.'}</p>
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-500/15 border border-red-400/30 rounded-2xl text-sm text-red-200" data-testid="login-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input id="username" type="text" value={username} onChange={e => setUsername(e.target.value)}
                className="w-full px-5 py-3 rounded-full glass-input text-sm focus:outline-none transition-colors"
                placeholder="Usuário" data-testid="login-username-input"
                autoComplete="username" disabled={loading} />
            </div>
            <div className="relative">
              <input id="senha" type={showSenha ? 'text' : 'password'} value={senha} onChange={e => setSenha(e.target.value)}
                className="w-full px-5 py-3 pr-11 rounded-full glass-input text-sm focus:outline-none transition-colors"
                placeholder="Senha" data-testid="login-senha-input"
                autoComplete="current-password" disabled={loading} />
              <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                onClick={() => setShowSenha(v => !v)} data-testid="login-toggle-senha">
                {showSenha ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="flex items-center justify-between px-1 text-xs text-white/85">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-white/40 bg-white/10 accent-white" />
                Lembrar usuário
              </label>
            </div>
            <button type="submit" disabled={loading}
              className="w-full justify-center py-3 mt-2 inline-flex items-center gap-2 glass-btn text-sm font-semibold rounded-full disabled:opacity-60"
              data-testid="login-submit-btn">
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Entrando...
                </span>
              ) : (<><LogIn size={16} /> Entrar</>)}
            </button>
          </form>
        </div>
        <p className="text-center text-xs text-slate-200 mt-4 drop-shadow" data-testid="login-footer-credit">
          {tenant.nomeDoSistema || 'Gastify'} · Feito por{' '}
          <a href="https://akuant.com/" target="_blank" rel="noopener noreferrer"
            className="text-white hover:text-emerald-300 font-semibold underline-offset-2 hover:underline transition-colors"
            data-testid="akuant-link">
            Akuant Solutions
          </a>{' '}&copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
